import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { decisionService } from '@/shared/services/decisionService';
import { usePreviewLock } from '@/shared/hooks/usePreviewLock';
import { formatReleaseMoment } from '@/shared/utils/deadline';
import DecisionEmailPreview from './DecisionEmailPreview';

export const MESSAGE_MAX = 5000;
const PREVIEW_DELAY_MS = 400;

const MESSAGE_ID = 'decision-email-message';

/**
 * The message box for the decision email, the fixed opening above it and the
 * server's rendering of the whole email below. `draft` is the server's
 * answer for `type`, or null when it could not be loaded; the box holds
 * `message` whatever the draft said. The preview is requested with the box's
 * exact text, 400 ms after the last change and at once when the draft or the
 * outcome changes. An answer older than the last request is dropped.
 */
export default function DecisionEmailComposer({
    applicationId,
    programType,
    type,
    awardedCount,
    draft,
    message,
    onMessageChange,
    disabled = false,
    loading = false,
    messageRef,
}) {
    const previewLocked = usePreviewLock();
    const [emails, setEmails] = useState([]);
    const [status, setStatus] = useState('loading');
    const requestRef = useRef(0);
    const lastKeyRef = useRef(null);

    useEffect(() => {
        if (previewLocked) {
            setStatus('locked');
            return undefined;
        }
        if (loading) return undefined;
        const key = `${type}|${draft ? 'draft' : 'none'}`;
        const immediate = key !== lastKeyRef.current;
        lastKeyRef.current = key;
        const run = async () => {
            const id = ++requestRef.current;
            try {
                const answer = await decisionService.previewDecisionEmail({
                    applicationId,
                    programType,
                    type,
                    messageToFaculty: message,
                    awardedCount,
                });
                if (id !== requestRef.current) return;
                if (!Array.isArray(answer?.emails)) throw new Error('shape');
                setEmails(answer.emails);
                setStatus('ready');
            } catch (_err) {
                if (id !== requestRef.current) return;
                setStatus('error');
            }
        };
        let timer = null;
        if (immediate) {
            // The old preview belongs to another draft or outcome
            setStatus('loading');
            run();
        } else {
            timer = setTimeout(run, PREVIEW_DELAY_MS);
        }
        return () => {
            clearTimeout(timer);
            requestRef.current += 1;
        };
    }, [
        applicationId,
        programType,
        type,
        awardedCount,
        draft,
        message,
        loading,
        previewLocked,
    ]);

    const length = message.length;
    const over = length > MESSAGE_MAX;
    const countId = `${MESSAGE_ID}-count`;
    const helpId = `${MESSAGE_ID}-help`;

    return (
        <div className="mt-3">
            {draft && (
                <div className="mb-3">
                    <p className="fw-semibold mb-1">The email starts with:</p>
                    {(draft.fixedParagraphs || []).map((paragraph, index) => (
                        <p key={index} className="mb-1 text-break">
                            {paragraph}
                        </p>
                    ))}
                </div>
            )}
            <Form.Group controlId={MESSAGE_ID} className="mb-2">
                <Form.Label>Message to the faculty member</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={6}
                    ref={messageRef}
                    value={message}
                    onChange={(e) => onMessageChange(e.target.value)}
                    disabled={disabled || loading}
                    aria-describedby={`${helpId} ${countId}`}
                />
                <Form.Text id={helpId} muted as="div">
                    Faculty read this after the sentence that states the
                    decision. Leave a blank line between paragraphs. Your review
                    notes are never included.
                </Form.Text>
                <Form.Text
                    id={countId}
                    as="div"
                    className={over ? 'text-danger' : 'text-body-secondary'}
                >
                    {length.toLocaleString('en-US')} of{' '}
                    {MESSAGE_MAX.toLocaleString('en-US')} characters
                    {over && '. This is too long to send. Shorten the message.'}
                </Form.Text>
            </Form.Group>
            {loading && (
                <p role="status" className="text-body-secondary mb-2">
                    Loading the program&apos;s email text...
                </p>
            )}
            {draft && (
                <p className="mb-3">
                    {draft.sendsAt
                        ? `Faculty get this email on ${formatReleaseMoment(
                              draft.sendsAt
                          )}, when the ${programType.toUpperCase()} program releases its decisions.`
                        : 'Faculty get this email as soon as you confirm.'}
                </p>
            )}
            <h3 className="h6 fw-bold">Preview of the email</h3>
            <DecisionEmailPreview emails={emails} status={status} />
        </div>
    );
}
